<ng-container *transloco="let t; read: 'monitoring'">
  <!-- Live-screenshot display — shared by the Detailed tile grid and the
       detail rail, both fed by the same `FleetSnapshot` shape (arrives
       inline on both /monitoring/fleet and /monitoring/{clientId}/detail),
       so a device's live image looks and behaves identically wherever it
       shows up on this page. Never infer "no image" from
       `status !== 'uploaded'` — `imageUrl` can be a fallback to an older
       snapshot while `status` is 'pending'/'failed'. See `FleetSnapshot`'s
       doc comment.

       Two sizing modes, picked by the `fixed` context flag:
        - fixed: true (tile grid) — the frame fills its parent's FIXED
          height and the capture renders object-contain inside it, so an
          image can never change the card's height (DS DisplayThumbnail:
          the card owns its size, the screenshot fits into it).
        - fixed: false (detail rail) — per design_handoff_snapshot/
          README.md, e-paper panels are portrait (3/4) or landscape (4/3),
          never assume one: the frame is full container width with height
          derived from orientation (detected from the loaded image itself,
          see `snapshotOrientation`'s doc comment — there's no orientation
          field on the API), and the capture FILLS that frame
          (`object-fit: fill` — the source ratio already matches the frame,
          nothing to crop or letterbox).
       Either way the capture is smoothed on upscale and rendered grayscale
       (e-paper is monochrome). `radius`/`chip` differ per call site. -->
  <ng-template #snapshotView let-snap="snap" let-radius="radius" let-chip="chip" let-dimmed="dimmed" let-fixed="fixed">
    <div
      class="relative w-full overflow-hidden bg-[#F0F0F0] shadow-[inset_0_0_0_1px_rgba(0,0,0,0.2)]"
      [style.borderRadius.px]="radius"
      [style.aspectRatio]="fixed ? null : snapshotAspectRatio(snap.imageUrl)"
      [class.h-full]="fixed"
      [class.opacity-50]="dimmed"
    >
      @if (snap.imageUrl && !snapshotFailed(snap.imageUrl)) {
        <img
          [src]="snap.imageUrl"
          [alt]="t('rail.latestImageAlt')"
          (load)="onSnapshotLoad($event, snap.imageUrl)"
          (error)="onSnapshotError(snap.imageUrl)"
          class="h-full w-full grayscale"
          [class.object-contain]="fixed"
          [class.object-fill]="!fixed"
          style="image-rendering: auto"
        />
        @if (snapshotFetching(snap.imageUrl)) {
          <!-- Browser still downloading this capture — the card is fine, only
               the image is on its way. Cleared by the img's load/error event. -->
          <div class="absolute inset-0 flex items-center justify-center">
            <app-spinner [size]="20" class="text-ink-3" />
          </div>
        }
        @if (snap.status === 'pending') {
          <span class="absolute bottom-1.5 right-1.5 flex items-center gap-1 rounded-full bg-scrim-control px-2 py-0.5 text-[11px] text-bar-ink">
            <app-spinner [size]="12" />
            {{ t('rail.latestImagePending') }}
          </span>
        }
        @if (chip && snapshotOrientationLabel(snap.imageUrl); as orientationLabel) {
          <span
            class="absolute bottom-1 right-1 rounded-[4px] bg-white/90 px-[5px] py-px text-[9px] font-semibold text-[#4D4D4D] shadow-[inset_0_0_0_1px_rgba(0,0,0,0.2)]"
          >
            {{ orientationLabel }}
          </span>
        }
      } @else if (snap.status === 'pending') {
        <div class="flex h-full w-full items-center justify-center">
          <span class="text-[12px] font-semibold text-ink-3">{{ t('rail.latestImagePending') }}</span>
        </div>
      } @else if (snap.status === 'failed' || snapshotFailed(snap.imageUrl)) {
        <div class="flex h-full w-full items-center justify-center">
          <span class="text-[12px] font-semibold text-danger">{{ t('rail.latestImageFailed') }}</span>
        </div>
      } @else {
        <div class="flex h-full w-full items-center justify-center">
          <span class="text-[12px] text-[#999999]">{{ t('rail.noCaptureYet') }}</span>
        </div>
      }
    </div>
  </ng-template>

  <app-grid-page [title]="t('title')">
    <div class="flex h-full min-h-0">
      <!-- Main content card — same DS card as the grid (16px radius, hairline
           edge); the tab strip sits inside it like the grid's toolbar row. -->
      <div
        class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-card border border-hairline bg-card"
      >
      <!-- Tab strip — DS Tab, same treatment as the grid's tab row: Figtree
           700 16/20 uppercase, 48px row, active = ink-1 + 4px action
           underline, passive = ink-2 with brand hover. -->
      <div class="flex h-12 flex-none items-stretch gap-0.5 border-b border-hairline px-3">
        @for (tabId of ['fleet', 'map', 'alerts', 'reports']; track tabId) {
          <button
            type="button"
            (click)="onTab($any(tabId))"
            class="relative flex items-center gap-2 border-none bg-transparent px-4"
            [class.text-ink-1]="tab() === tabId"
            [class.text-ink-2]="tab() !== tabId"
            [class.hover:text-brand]="tab() !== tabId"
          >
            <span class="whitespace-nowrap font-display text-h3 font-bold uppercase"
              >{{ t('tabs.' + tabId) }}</span
            >
            @if (tab() === tabId) {
              <div class="absolute inset-x-0 -bottom-px h-1 bg-action"></div>
            }
          </button>
        }
      </div>

      <!-- Tab content — hero and control rows stay fixed; only the tile
           matrix scrolls, so cards clip at the card edge like the design. -->
      <div class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          @switch (tab()) {
            @case ('fleet') {
              <!-- Fleet-health metric band — flat DashboardMetric columns
                   separated by hairlines (design 583:20366; no gauge, no
                   sunken card). Metrics with no data source yet render a
                   dash bar + N/A, honest placeholders as before. -->
              <div class="flex flex-none items-stretch gap-8 overflow-x-auto p-6">
                <div class="flex flex-none flex-col border-r border-hairline pr-8">
                  <span class="font-display text-h2 font-bold text-ink-1">{{ t('hero.title') }}</span>
                  <span class="mt-1 font-display text-h0 font-medium text-ink-1">{{
                    healthPct() === null ? t('placeholders.dash') : healthPct() + '%'
                  }}</span>
                  <span class="mt-1 max-w-[300px] text-[14px] leading-6 text-ink-2">{{
                    t(heroLineKey(), { count: attentionCount() })
                  }}</span>
                </div>
                <div class="flex flex-none flex-col gap-1 border-r border-hairline pr-8">
                  <span class="min-h-8 max-w-[120px] text-[12px] font-bold uppercase tracking-wide text-ink-1">{{ t('hero.reachable') }}</span>
                  <span class="font-display text-[40px] font-medium leading-[48px] text-ink-1">{{ reachableCount() }}/{{ allTiles().length }}</span>
                  <span class="text-[14px] text-ink-2">{{ t('hero.reachableNote', { offline: offlineCount() }) }}</span>
                </div>
                <div class="flex flex-none flex-col gap-1 border-r border-hairline pr-8">
                  <span class="min-h-8 max-w-[120px] text-[12px] font-bold uppercase tracking-wide text-ink-1">{{ t('hero.needsAttention') }}</span>
                  <span
                    class="font-display text-[40px] font-medium leading-[48px]"
                    [class.text-danger]="attentionCount() > 0"
                    [class.text-ink-1]="attentionCount() === 0"
                    >{{ attentionCount() }}</span
                  >
                  <span class="text-[14px] text-ink-2">{{ t('hero.needsAttentionNote') }}</span>
                </div>
                <div class="flex flex-none flex-col gap-1">
                  <span class="min-h-8 max-w-[120px] text-[12px] font-bold uppercase tracking-wide text-ink-1">{{ t('hero.openAlerts') }}</span>
                  <div class="flex h-12 items-center"><span class="h-1 w-9 rounded-full bg-ink-1"></span></div>
                  <span class="text-[14px] text-ink-3">{{ t('placeholders.na') }}</span>
                </div>
              </div>

              <!-- Control row: status filter + model dropdown left; legend and
                   density toggle right. Segmented groups are the DS
                   SegmentedControl (297:3532): grey group, black active
                   segment raised above it. -->
              <div class="flex flex-none flex-wrap items-center justify-between gap-x-4 gap-y-2 border-y border-hairline px-6 py-3">
                <div class="flex items-center gap-3">
                  <div class="flex items-center overflow-hidden rounded-control bg-surface-3 shadow-hairline">
                    @for (f of ['all', 'attention', 'offline']; track f) {
                      <button
                        type="button"
                        (click)="onStatusFilter($any(f))"
                        class="whitespace-nowrap rounded-control px-4 py-1.5 text-[13px]"
                        [class.bg-action]="statusFilter() === f"
                        [class.text-action-text]="statusFilter() === f"
                        [class.font-semibold]="statusFilter() === f"
                        [class.shadow-raised]="statusFilter() === f"
                        [class.text-ink-2]="statusFilter() !== f"
                        [class.hover:text-ink-1]="statusFilter() !== f"
                      >
                        {{ t('filters.' + f) }}
                      </button>
                    }
                  </div>
                  <div class="w-[180px]">
                    <app-select
                      [ngModel]="model()"
                      (ngModelChange)="onModelChange($event)"
                      [options]="modelOptions()"
                    />
                  </div>
                </div>
                <div class="flex flex-none items-center gap-5">
                  @for (l of toolbarLegend(); track l.status) {
                    <div class="flex items-center gap-2">
                      <span class="h-2.5 w-2.5 flex-none rounded-full" [class]="dotClass(l.status)"></span>
                      <span class="text-[13px] text-ink-1">{{ l.label }}</span>
                      <span class="text-[13px] font-bold text-ink-1">{{ l.count }}</span>
                    </div>
                  }
                  <div class="h-6 w-px flex-none bg-hairline"></div>
                  <div class="flex items-center overflow-hidden rounded-control bg-surface-3 shadow-hairline">
                    @for (d of ['detailed', 'compact']; track d) {
                      <button
                        type="button"
                        (click)="onDensity($any(d))"
                        class="flex h-8 w-9 items-center justify-center rounded-control"
                        [class.bg-action]="density() === d"
                        [class.text-action-text]="density() === d"
                        [class.shadow-raised]="density() === d"
                        [class.text-ink-2]="density() !== d"
                        [title]="t('density.' + d)"
                      >
                        <app-icon [name]="d === 'compact' ? 'icon-view-condensed' : 'icon-view-normal'" [size]="18" />
                      </button>
                    }
                  </div>
                </div>
              </div>

              <!-- Tile matrix — the only scrolling region on this tab -->
              <div class="min-h-0 flex-1 overflow-y-auto p-6" [attr.aria-busy]="isLoading() || null">
                @if (density() === 'compact') {
                  @if (showSkeleton()) {
                    <!-- Cold load: bone cards on the real grid geometry. -->
                    <div
                      class="grid-skeleton grid auto-rows-min grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8"
                      aria-hidden="true"
                    >
                      @for (bone of skeletonTiles; track $index) {
                        <div class="h-[92px] animate-pulse rounded-control bg-surface-2"></div>
                      }
                    </div>
                  } @else {
                    <div
                      class="grid auto-rows-min grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8"
                      [class.grid-refetching]="isRefetching()"
                    >
                      @for (tile of tiles(); track tile.clientId) {
                        <button
                          type="button"
                          (click)="selectTile(tile)"
                          (dblclick)="openTile(tile)"
                          class="flex h-[92px] flex-col gap-1 overflow-hidden rounded-control p-2.5 text-left"
                          [class]="tintClass(tile.status)"
                          [class.ring-2]="selectedTileId() === tile.clientId"
                          [class.ring-brand]="selectedTileId() === tile.clientId"
                        >
                          <div class="flex items-center justify-between">
                            <span class="text-[12px] font-bold tracking-wide" [class]="textClass(tile.status)">{{ tile.clientId }}</span>
                            <span class="h-2 w-2 flex-none rounded-full" [class]="dotClass(tile.status)"></span>
                          </div>
                          <span class="truncate text-[12px] font-semibold text-ink-1">{{ tile.label }}</span>
                          <span class="truncate text-[12px] text-ink-2">{{ tileMeta(tile) }}</span>
                        </button>
                      } @empty {
                        <div class="col-span-full flex items-center justify-center py-16 text-[13px] text-ink-3">
                          {{ t('pager.empty') }}
                        </div>
                      }
                    </div>
                  }
                } @else {
                  <!-- Detailed: DS DisplayThumbnail (584:21131) — grey card,
                       name zone, dark image bezel, status + icon vitals row.
                       The image zone is a FIXED height and the capture is
                       contained inside it (snapshotView's `fixed` mode), so
                       every card is the same size regardless of what its
                       snapshot looks like — a uniform row grid, like the
                       design, with no masonry needed. -->
                  @if (showSkeleton()) {
                    <!-- Cold load: bone cards mirroring the real card frame
                         (58px name zone, 140px image zone, vitals row) so the
                         layout doesn't move when tiles land. -->
                    <div
                      class="grid-skeleton grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"
                      aria-hidden="true"
                    >
                      @for (bone of skeletonTiles; track $index) {
                        <div class="flex w-full flex-col overflow-hidden rounded-control bg-surface-3 p-2 shadow-hairline">
                          <span class="flex min-h-[58px] w-full items-start px-1 pb-2 pt-0.5">
                            <span class="h-4 w-3/4 animate-pulse rounded bg-surface-2"></span>
                          </span>
                          <div class="h-[140px] w-full flex-none animate-pulse rounded-[4px] bg-surface-2"></div>
                          <div class="flex w-full items-center gap-2.5 px-1 pb-0.5 pt-2">
                            <span class="h-3 w-3 flex-none animate-pulse rounded-full bg-surface-2"></span>
                            <span class="h-3 w-16 animate-pulse rounded bg-surface-2"></span>
                          </div>
                        </div>
                      }
                    </div>
                  } @else if (tiles().length === 0) {
                    <div class="flex items-center justify-center py-16 text-[13px] text-ink-3">
                      {{ t('pager.empty') }}
                    </div>
                  } @else {
                    <div
                      class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"
                      [class.grid-refetching]="isRefetching()"
                    >
                      @for (tile of tiles(); track tile.clientId) {
                        <button
                          type="button"
                          (click)="selectTile(tile)"
                          (dblclick)="openTile(tile)"
                          class="flex w-full flex-col overflow-hidden rounded-control bg-surface-3 p-2 text-left shadow-hairline"
                          [class.ring-2]="selectedTileId() === tile.clientId"
                          [class.ring-brand]="selectedTileId() === tile.clientId"
                        >
                          <span class="line-clamp-2 min-h-[58px] w-full px-1 pb-2 pt-0.5 text-[16px] font-semibold leading-6 text-ink-1">{{
                            tile.label
                          }}</span>
                          <div class="h-[140px] w-full flex-none rounded-[4px] bg-surface-4 p-2">
                            <ng-container
                              *ngTemplateOutlet="
                                snapshotView;
                                context: {
                                  snap: tile.snapshot,
                                  radius: 2,
                                  chip: false,
                                  fixed: true,
                                  dimmed: tile.status === 'offline' || tile.status === 'shutdown'
                                }
                              "
                            ></ng-container>
                          </div>
                          <div class="flex w-full items-center gap-2.5 px-1 pb-0.5 pt-2">
                            <span class="flex min-w-0 flex-1 items-center gap-1.5">
                              <span class="h-3 w-3 flex-none rounded-full" [class]="dotClass(tile.status)"></span>
                              <span class="truncate text-[12px] font-semibold text-ink-2">{{ tileStatusLabel(tile.status) }}</span>
                            </span>
                            @if (tile.battery !== null) {
                              <span class="flex flex-none items-center gap-1 text-[12px] font-semibold text-ink-2">
                                <app-icon name="icon-battery" [size]="16" />{{ tile.battery }}%
                              </span>
                            }
                            @if (tile.signal !== null) {
                              <span class="flex flex-none items-center gap-1 text-[12px] font-semibold text-ink-2">
                                <app-icon name="icon-signal-wifi" [size]="16" />{{ tile.signal }}
                              </span>
                            }
                            @if (tile.lastSync) {
                              <span class="flex flex-none items-center gap-1 text-[12px] font-semibold text-ink-2">
                                <app-icon name="icon-update" [size]="16" />{{ relativeTime(tile.lastSync) }}
                              </span>
                            }
                          </div>
                        </button>
                      }
                    </div>
                  }
                }
              </div>

              <!-- Pager -->
              <div class="flex flex-none items-center justify-between border-t border-hairline px-6 py-3">
                <span class="text-[12px] text-ink-2">{{
                  t('pager.range', {
                    from: totalElements() === 0 ? 0 : page() * size() + 1,
                    to: page() * size() + tiles().length,
                    total: totalElements()
                  })
                }}</span>
                <div class="flex items-center gap-4">
                  <button
                    type="button"
                    (click)="prevPage()"
                    [disabled]="page() === 0"
                    class="rounded-control px-3 py-1 text-[12px] font-semibold text-ink-1 disabled:opacity-40 hover:bg-ink-1/10"
                    [title]="t('pager.previous')"
                  >
                    ←
                  </button>
                  <span class="text-[12px] font-semibold text-ink-1">{{
                    t('pager.page', { current: page() + 1, total: totalPages() || 1 })
                  }}</span>
                  <button
                    type="button"
                    (click)="nextPage()"
                    [disabled]="page() >= totalPages() - 1"
                    class="rounded-control px-3 py-1 text-[12px] font-semibold text-ink-1 disabled:opacity-40 hover:bg-ink-1/10"
                    [title]="t('pager.next')"
                  >
                    →
                  </button>
                </div>
              </div>
            }
            @case ('map') {
              <div class="flex min-h-0 min-w-0 flex-1 flex-col p-6">
              <!-- Control row: filter left, legend right (no density, no model dropdown) -->
              <div class="flex flex-none items-center justify-between pb-4">
                <div class="flex items-center rounded-control bg-sunken p-0.5">
                  @for (f of ['all', 'attention', 'offline']; track f) {
                    <button
                      type="button"
                      (click)="onStatusFilter($any(f))"
                      class="rounded-control px-4 py-1.5 text-[12px] font-semibold"
                      [class.bg-card]="statusFilter() === f"
                      [class.text-ink-1]="statusFilter() === f"
                      [class.shadow-hairline]="statusFilter() === f"
                      [class.text-ink-2]="statusFilter() !== f"
                    >
                      {{ t('filters.' + f) }}
                    </button>
                  }
                </div>
                <div class="flex flex-none items-center gap-6">
                  @for (l of legend(); track l.status) {
                    <div class="flex items-center gap-2">
                      <span class="h-3.5 w-3.5 flex-none rounded" [class]="tintClass(l.status)">
                        <span class="m-[3px] block h-2 w-2 rounded-full" [class]="dotClass(l.status)"></span>
                      </span>
                      <span class="text-[12px] font-semibold text-ink-2">{{ l.label }}</span>
                      <span class="text-[12px] font-bold text-ink-1">{{ l.count }}</span>
                    </div>
                  }
                </div>
              </div>

              <!-- Map canvas fills the remaining space -->
              <div class="relative min-h-0 flex-1 overflow-hidden rounded-card border border-hairline bg-sunken">
                @if (isLoadingFleetMap()) {
                  <!-- Flat placeholder — no interactive map, no stats strip,
                       while the whole-fleet pin set is still loading. -->
                  <div class="h-full w-full bg-[#F0F0F0]"></div>
                } @else {
                  <div
                    leaflet
                    class="h-full w-full"
                    [leafletOptions]="mapOptions"
                    [leafletLayers]="mapLayers()"
                    (leafletMapReady)="onMapReady($event)"
                  ></div>

                  <!-- Floating stats strip -->
                  <div
                    class="absolute bottom-4 left-4 z-[500] flex items-center gap-5 rounded-card bg-card/95 px-4 py-3 shadow-raised"
                  >
                    <div class="flex flex-col gap-0.5">
                      <span class="text-[11px] font-semibold uppercase tracking-wide text-ink-2">{{ t('hero.reachable') }}</span>
                      <span class="font-display text-lg font-bold text-ink-1">{{ reachableCount() }} / {{ allTiles().length }}</span>
                    </div>
                    <div class="h-8 w-px bg-hairline"></div>
                    <div class="flex flex-col gap-0.5">
                      <span class="text-[11px] font-semibold uppercase tracking-wide text-ink-2">{{ t('hero.needsAttention') }}</span>
                      <span class="font-display text-lg font-bold text-warning">{{ attentionCount() }}</span>
                    </div>
                    <div class="h-8 w-px bg-hairline"></div>
                    <div class="flex flex-col gap-0.5">
                      <span class="text-[11px] font-semibold uppercase tracking-wide text-ink-2">{{ t('hero.openAlerts') }}</span>
                      <span class="font-display text-lg font-bold text-ink-3">{{ t('placeholders.dash') }}</span>
                    </div>
                    <span class="text-[11px] text-ink-3 max-[700px]:hidden">{{ t('map.pinCount', { count: allTiles().length }) }}</span>
                  </div>
                }
              </div>
              </div>
            }
            @case ('alerts') {
              <div class="flex flex-1 items-center justify-center text-[14px] text-ink-3">
                {{ t('notSupported') }}
              </div>
            }
            @case ('reports') {
              <div class="flex flex-1 items-center justify-center text-[14px] text-ink-3">
                {{ t('notSupported') }}
              </div>
            }
          }
      </div>
      </div>

      <!-- Right rail content — shared between the lg+ side card and the
           compact-viewport bottom sheet. -->
      <ng-template #railContent>
          @if (selectedSummary(); as summary) {
            <!-- Selected device header — from `selectedSummary()`, known
                 synchronously (grid tile or map pin) even before /detail
                 resolves. -->
            <div class="flex items-start justify-between gap-3">
              <div class="flex flex-col gap-1.5">
                <span class="text-[11px] font-semibold uppercase tracking-wide text-ink-2">{{ summary.clientId }}</span>
                <span class="font-display text-2xl font-bold leading-7 text-ink-1">{{ summary.label }}</span>
              </div>
              <button
                type="button"
                (click)="clearSelection()"
                class="flex h-8 w-8 flex-none items-center justify-center rounded-control border border-hairline bg-card text-ink-1 hover:bg-sunken"
                [title]="t('rail.close')"
              >
                ✕
              </button>
            </div>

            <app-label-badge [dot]="statusDot(summary.status)">{{ statusLabelOrUnknown(summary.status) }}</app-label-badge>

            @if (isLoadingDetail() && !selectedTileDetail()) {
              <div class="flex flex-1 items-center justify-center py-10">
                <app-spinner [size]="24" class="text-ink-3" />
              </div>
            } @else if (selectedTileDetail(); as detail) {
              <ng-container
                *ngTemplateOutlet="
                  snapshotView;
                  context: {
                    snap: detail.snapshot,
                    radius: 8,
                    chip: false,
                    dimmed: summary.status === 'offline' || summary.status === 'shutdown'
                  }
                "
              ></ng-container>
              @if (detail.snapshot.imageUrl) {
                <!-- No dedicated per-snapshot capture timestamp exists on
                     `FleetSnapshot` — `lastSync` (the device's last
                     heartbeat) stands in as the closest available proxy. -->
                <span class="text-[11px] text-[#999999]">
                  {{ t('rail.captionCaptured', { age: relativeTime(detail.lastSync) }) }}
                  @if (snapshotOrientationLabel(detail.snapshot.imageUrl); as orientationLabel) {
                    · {{ orientationLabel }}
                  }
                  · {{ t('rail.captionRefresh') }}
                </span>
              }

              <div class="flex flex-col">
                @for (spec of [
                  { label: t('rail.specs.operator'), value: detail.operator },
                  { label: t('rail.specs.battery'), value: detail.battery !== null ? detail.battery + '%' : null },
                  { label: t('rail.specs.signal'), value: detail.signal },
                  { label: t('rail.specs.lastSync'), value: detail.lastSync ? relativeTime(detail.lastSync) : null },
                  { label: t('rail.specs.firmware'), value: detail.firmware }
                ]; track spec.label) {
                  <div class="flex items-center justify-between border-b border-hairline py-2.5">
                    <span class="text-[12px] text-ink-2">{{ spec.label }}</span>
                    <span class="text-[13px] font-semibold text-ink-1">{{ spec.value ?? t('placeholders.dash') }}</span>
                  </div>
                }
              </div>

              <div class="flex flex-col gap-2">
                <span class="text-[11px] font-semibold uppercase tracking-wide text-ink-2">{{ t('rail.openAlerts') }}</span>
                @if (detail.alerts.length === 0) {
                  <div class="rounded-control border border-hairline bg-card px-3 py-2.5 text-[12px] text-ink-3">
                    {{ t('rail.noAlertData') }}
                  </div>
                } @else {
                  @for (alert of detail.alerts; track alert.id) {
                    <div class="flex items-start gap-2 rounded-control border border-hairline bg-card px-3 py-2.5">
                      <span
                        class="mt-1 h-2 w-2 flex-none rounded-full"
                        [class]="alert.severity === 'error' ? 'bg-danger' : 'bg-caution'"
                      ></span>
                      <div class="flex flex-col">
                        <span class="text-[12px] text-ink-1">{{ alert.message }}</span>
                        <span class="text-[11px] text-ink-3">{{ alert.occurredAt }}</span>
                      </div>
                    </div>
                  }
                }
              </div>

              <div class="mt-1 flex gap-2">
                <button type="button" class="flex-1 rounded-control bg-action px-4 py-2.5 text-[12px] font-semibold text-action-text hover:brightness-90">
                  {{ t('rail.refresh') }}
                </button>
                <button
                  type="button"
                  (click)="openSelectedDevice()"
                  class="flex-1 rounded-control border border-hairline bg-card px-4 py-2.5 text-[12px] font-semibold text-ink-1 hover:bg-sunken"
                >
                  {{ t('rail.openDisplay') }}
                </button>
              </div>
            }
          }
      </ng-template>

      <!-- Right rail — detail-on-demand: only rendered while a device is
           selected (no fleet-summary empty state; the toolbar legend and
           hero already carry those numbers), so the tile matrix gets the
           full row width otherwise. Its own DS card, gapped from the main
           card like the grid's detail panel; below lg the selection opens
           as a bottom sheet instead (mirrors the grid's compact detail).
           Width clamps against the row (like the grid's split pane) so the
           rail can't crowd the fleet view on smaller laptops. -->
      @if (selectedSummary()) {
        <div
          animate.enter="rail-panel-in"
          animate.leave="rail-panel-out"
          class="ml-4 hidden w-[min(380px,45%)] flex-none overflow-hidden rounded-card border border-hairline bg-card lg:flex"
          style="--monitoring-rail-width: min(380px, 45%)"
        >
          <div class="flex h-full w-full flex-col gap-5 overflow-y-auto p-6">
            <ng-container [ngTemplateOutlet]="railContent" />
          </div>
        </div>
      }

      @if (compactDetail() && selectedSummary()) {
        <div
          animate.enter="rail-scrim-in"
          animate.leave="rail-scrim-out"
          class="fixed inset-0 z-40 bg-scrim-control lg:hidden"
          (click)="clearSelection()"
          aria-hidden="true"
        ></div>
      }
      @if (compactDetail() && selectedSummary()) {
        <section
          role="dialog"
          aria-modal="true"
          animate.enter="rail-sheet-in"
          animate.leave="rail-sheet-out"
          class="fixed inset-x-0 bottom-0 top-14 z-40 flex flex-col gap-5 overflow-y-auto rounded-t-card border border-hairline bg-card p-6 shadow-modal lg:hidden"
        >
          <button
            type="button"
            (click)="clearSelection()"
            [title]="t('rail.close')"
            class="absolute right-4 top-4 flex h-[30px] w-[30px] items-center justify-center rounded-control border-none bg-transparent text-ink-3 hover:bg-sunken"
          >
            <app-icon name="icon-x" [size]="17" />
          </button>
          <ng-container [ngTemplateOutlet]="railContent" />
        </section>
      }
    </div>
  </app-grid-page>
</ng-container>
